import React, { useEffect, useState, useContext } from 'react';
import { connect } from 'react-redux';
import cx from 'classnames';
import PropTypes from 'prop-types';
import { SubmitButton } from '../Button';
import {
  setCaptchaResponse,
  setDay,
  setPartySize,
  setReservation,
  setVenueId,
  setVenueName,
} from '../../reducers/bookingSlice';
import TranslationProvider, { getTranslation } from '../../providers/TranslationProvider';
import RouterNavigateContext from '../../../../widget/app/modules/contexts/RouterNavigate';
import getServiceDateFromResyToken from '../../utils/resyToken';
import { getURLSearchParams } from '../../utils/searchParamUtils';
import { handleNavigate } from '../../utils/locationUtils';
import './Captcha.scss';

const getGrecaptcha = () => {
  if (window.grecaptcha && window.grecaptcha.render) {
    return window.grecaptcha;
  }

  return undefined;
};

const renderCaptcha = () => {
  const grecaptcha = getGrecaptcha();

  if (!grecaptcha) {
    setTimeout(renderCaptcha, 500);
    return;
  }
  grecaptcha.render('captcha__container', {
    sitekey: '6Lfw-dIZAAAAAESRBH4JwdgfTXj5LlS1ewlvvCYe',
  });
};

const checkCaptchaResponse = () => {
  const grecaptcha = getGrecaptcha();
  const captchaResponse = grecaptcha?.getResponse();

  return captchaResponse;
};

const getSuccessPath = (searchParams, reservation = {}) => {
  const addOns = searchParams.get('addOns');
  const venueId = searchParams.get('venueId');
  if (addOns === 'true' || reservation.hasAddOns || reservation.hasMenus) {
    return `/venues/${venueId}/add-ons/${reservation.templateId}`;
  }
  return '/reservation-details';
};

const Captcha = ({
  AutomationMode,
  dispatch,
  flushRoute,
  reservationFromStore,
}) => {
  useEffect(() => {
    const captchaElement = document.getElementById('captcha__container');
    if (!AutomationMode && !captchaElement?.children?.length > 0) {
      renderCaptcha();
    }
  }, []);
  const [error, setError] = useState();

  let reservation = reservationFromStore;
  const searchParams = getURLSearchParams();
  const reservationFromParams = searchParams.get('reservation');

  if (!reservation && reservationFromParams) {
    reservation = JSON.parse(reservationFromParams);
  }

  const routerNavigate = useContext(RouterNavigateContext);
  const handleSubmit = () => {
    const captchaResponse = checkCaptchaResponse();
    const date = getServiceDateFromResyToken(reservation?.token, searchParams.get('date'));
    const seats = searchParams.get('seats') || undefined;
    const venueId = searchParams.get('venueId') || undefined;
    if (captchaResponse) {
      setError(undefined);
      dispatch(setCaptchaResponse(captchaResponse));

      dispatch(setReservation(reservation));
      dispatch(setDay(date));
      dispatch(setPartySize(seats));
      dispatch(setVenueId(venueId));
      dispatch(setVenueName(reservation.venueName));

      const successPath = getSuccessPath(searchParams, reservation);
      handleNavigate(successPath, routerNavigate, flushRoute);
    } else {
      setError(getTranslation({
        translationKey: 'captcha_verify_error',
        fallback: 'Please verify that you are not a robot.',
      }));
    }
  };

  return (
    <div className="Captcha">
      <div className="Captcha__section">
        <TranslationProvider translationKey="captcha_header">
          <h1 className="Captcha__title">Security Check</h1>
        </TranslationProvider>
        <TranslationProvider translationKey="captcha_subheader">
          <div className="Captcha__subheader">Please complete this step to continue booking.</div>
        </TranslationProvider>
      </div>

      <hr />

      <div className="Captcha__section">
        <div className={cx('captcha__outer__container', { 'captcha__outer__container--error': error })}>
          <div id="captcha__container" className="captcha__container" />
          {error && <div className="Captcha__error">{error}</div>}
        </div>

        <TranslationProvider
          translationKey="captcha_body"
          translationValues={{ venueName: reservation.venueName || 'this venue' }}
        >
          <div className="Captcha__body">
            To help us maintain a fair experience for everyone, this step is required to book reservations at
            {reservation.venueName}
            .
          </div>
        </TranslationProvider>
      </div>

      <hr />

      <div className="Captcha__section">
        <SubmitButton onClick={handleSubmit}>
          {getTranslation({
            translationKey: 'continue',
            fallback: 'Continue',
          })}
        </SubmitButton>
      </div>
    </div>
  );
};

const captchaPropTypes = {
  AutomationMode: PropTypes.bool,
  dispatch: PropTypes.func.isRequired,
  flushRoute: PropTypes.func,
  reservationFromStore: PropTypes.shape({}),
};

Captcha.defaultProps = {
  AutomationMode: false,
  flushRoute: () => {},
  reservationFromStore: undefined,
};

const mapStateToProps = ({ booking }) => ({
  reservationFromStore: booking.reservation,
});

Captcha.displayName = 'Captcha';
Captcha.propTypes = captchaPropTypes;

export default connect(mapStateToProps)(Captcha);
export { captchaPropTypes, Captcha as UnconnectedCaptcha };
